import { SpaPropertyEnum } from "../../constants/NavbarList";
import { ExcelExportSheetConfig } from "../types";
import {
  SpaExcelHeader,
  SpaExcelSheetName,
  SpaExcelSourceField,
} from "../enums/spaExcel";
import {
  spaExcelColumn,
  spaExcelFile,
  spaExcelSheet,
  spaExcelWorksheet,
} from "../enums/typedExcel";
import {
  EXCEL_DEFAULT_START_DATE,
  EXCEL_DEFAULT_END_DATE_PLUS_3_YEARS,
} from "../constants/dateDefaults";
import {
  ExcelBoolean,
  ExcelBooleanString,
  ExcelEmptyDefault,
} from "../constants/defaults";
import { DEFAULT_VENDOR_CONTACT_TYPE } from "../../constants/VendorContactTypes";
import {
  mapVendorSetupContactRows,
  mapVendorSetupVendorRows,
} from "../mapping/vendorSetupRows";
import {
  mapTherapistContactRows,
  mapTherapistMainRows,
} from "../mapping/therapistContactRows";
import {
  mapServiceAvailableDaysRows,
  mapServiceDetailRows,
  mapServiceEquipmentRows,
  mapServicePriceTypeRows,
} from "../mapping/serviceDetailRows";
import { mapGuestTypeExportRows } from "../mapping/guestTypeRows";
import {
  DEFAULT_RETAIL_ITEM_NUMBER,
  SPA_SERVICES_CATEGORY_GROUP,
} from "../../constants/DefaultRetailItems";
import {
  mapRetailItemRows,
  retailItemExcelColumns,
} from "../mapping/retailItemRows";

const dayAvailabilityColumns = () => [
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnMonday,
    SpaExcelSourceField.AvailableMon,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnTuesday,
    SpaExcelSourceField.AvailableTue,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnWednesday,
    SpaExcelSourceField.AvailableWed,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnThursday,
    SpaExcelSourceField.AvailableThu,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnFriday,
    SpaExcelSourceField.AvailableFri,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnSaturday,
    SpaExcelSourceField.AvailableSat,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnSunday,
    SpaExcelSourceField.AvailableSun,
    ExcelBoolean.False,
  ),
];

/** Visual One ServiceAvailableDays: ServiceCode + IsAvailableOnMonday … Sunday. */
const serviceAvailableDaysColumns = [
  spaExcelColumn(SpaExcelHeader.ServiceCode, SpaExcelSourceField.ServiceCode),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnMonday,
    SpaExcelSourceField.AvailableMon,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnTuesday,
    SpaExcelSourceField.AvailableTue,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnWednesday,
    SpaExcelSourceField.AvailableWed,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnThursday,
    SpaExcelSourceField.AvailableThu,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnFriday,
    SpaExcelSourceField.AvailableFri,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnSaturday,
    SpaExcelSourceField.AvailableSat,
    ExcelBoolean.False,
  ),
  spaExcelColumn(
    SpaExcelHeader.IsAvailableOnSunday,
    SpaExcelSourceField.AvailableSun,
    ExcelBoolean.False,
  ),
];

/**
 * Spa Excel export definitions — mapped to Spa onboarding payload keys only.
 * File/worksheet names follow the import template spec; sourceField maps onboarding data.
 */
export const spaExcelExportSheets: ExcelExportSheetConfig[] = [
  spaExcelSheet(SpaPropertyEnum.outletSetup, SpaExcelSheetName.Outlet, [
    spaExcelColumn(SpaExcelHeader.OutletName, SpaExcelSourceField.OutletName),
    spaExcelColumn(
      SpaExcelHeader.ProfitCenter,
      SpaExcelSourceField.ProfitCenterID,
      ExcelEmptyDefault.Empty,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsDefault,
      ExcelEmptyDefault.Empty,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.AutoReceipt,
      SpaExcelSourceField.AutoPrintsReceipts,
      ExcelBooleanString.True,
    ),
    spaExcelColumn(
      SpaExcelHeader.POSEndPoint,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    spaExcelColumn(
      SpaExcelHeader.TerminalId,
      SpaExcelSourceField.TerminalID,
      ExcelEmptyDefault.Empty,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsActive,
      SpaExcelSourceField.Active,
      ExcelBooleanString.True,
    ),
  ]),

  spaExcelSheet(SpaPropertyEnum.retailCategories, SpaExcelSheetName.Category, [
    spaExcelColumn(SpaExcelHeader.Name, SpaExcelSourceField.CategoryName),
    spaExcelColumn(
      SpaExcelHeader.CategoryGroupName,
      SpaExcelSourceField.CategoryGroup,
      SPA_SERVICES_CATEGORY_GROUP,
    ),
    spaExcelColumn(
      SpaExcelHeader.EligibleForDiscount,
      SpaExcelSourceField.EligibleForDiscount,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsActive,
      SpaExcelSourceField.Active,
      ExcelBoolean.True,
    ),
  ]),

  spaExcelSheet(
    SpaPropertyEnum.serviceGroups,
    SpaExcelSheetName.ServiceGroups,
    [
      spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.ServiceGroupCode),
      spaExcelColumn(
        SpaExcelHeader.Description,
        SpaExcelSourceField.ServiceGroupName,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.serviceEquipment,
    SpaExcelSheetName.Equipments,
    [
      spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.EquipmentCode),
      spaExcelColumn(
        SpaExcelHeader.Description,
        SpaExcelSourceField.EquipmentDescription,
      ),
      spaExcelColumn(SpaExcelHeader.Quantity, SpaExcelSourceField.Quantity, 0),
    ],
  ),
  spaExcelSheet(SpaPropertyEnum.cancelReasons, SpaExcelSheetName.CancelReason, [
    spaExcelColumn(
      SpaExcelHeader.ReasonCode,
      SpaExcelSourceField.CancelReasonDescription,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsActive,
      SpaExcelSourceField.Active,
      ExcelBoolean.True,
    ),
  ]),

  spaExcelSheet(SpaPropertyEnum.breakTypes, SpaExcelSheetName.BreakTypes, [
    spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.BreakCode),
    spaExcelColumn(SpaExcelHeader.Description, SpaExcelSourceField.BreakName),
    spaExcelColumn(SpaExcelHeader.Color, SpaExcelSourceField.Color),
    spaExcelColumn(
      SpaExcelHeader.IsUtilized,
      SpaExcelSourceField.IsUtilized,
      ExcelBoolean.False,
    ),
  ]),

  spaExcelSheet(
    SpaPropertyEnum.lostDeniedReasons,
    SpaExcelSheetName.LostDeniedReason,
    [
      spaExcelColumn(
        SpaExcelHeader.Code,
        SpaExcelSourceField.ReasonDescription,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.serviceMedicalConditions,
    SpaExcelSheetName.MedicalConditions,
    [
      spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.ConditionCode),
      spaExcelColumn(
        SpaExcelHeader.Description,
        SpaExcelSourceField.ConditionDescription,
      ),
      spaExcelColumn(
        SpaExcelHeader.Warning,
        SpaExcelSourceField.ConditionWarning,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsPrivate,
        SpaExcelSourceField.PrivateCondition,
        ExcelBooleanString.False,
      ),
    ],
  ),

  spaExcelSheet(SpaPropertyEnum.serviceLocations, SpaExcelSheetName.Locations, [
    spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.LocationCode),
    spaExcelColumn(
      SpaExcelHeader.Description,
      SpaExcelSourceField.LocationName,
    ),
    spaExcelColumn(SpaExcelHeader.MaxGuests, SpaExcelSourceField.Capacity, "0"),
    spaExcelColumn(
      SpaExcelHeader.Comments,
      SpaExcelSourceField.LocationDescription,
      ExcelEmptyDefault.Empty,
    ),
  ]),

  spaExcelSheet(
    SpaPropertyEnum.discountTypes,
    SpaExcelSheetName.DiscountTypes,
    [
      spaExcelColumn(
        SpaExcelHeader.DiscountType,
        SpaExcelSourceField.DiscountType,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.categoryGroups,
    SpaExcelSheetName.CategoryGroup,
    [
      spaExcelColumn(
        SpaExcelHeader.CategoryGroup,
        SpaExcelSourceField.CategoryGroup,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.discountReasons,
    SpaExcelSheetName.DiscountReasons,
    [
      spaExcelColumn(
        SpaExcelHeader.DiscountReason,
        SpaExcelSourceField.DiscountReason,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.quickSaleCategories,
    SpaExcelSheetName.QuickSaleCategory,
    [
      spaExcelColumn(
        SpaExcelHeader.Category,
        SpaExcelSourceField.QuickSaleCategory,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
      spaExcelColumn(SpaExcelHeader.Outlets, SpaExcelSourceField.OutletName),
    ],
  ),

  spaExcelSheet(SpaPropertyEnum.taxes, SpaExcelSheetName.Tax, [
    spaExcelColumn(SpaExcelHeader.OutletName, SpaExcelSourceField.Outlet),
    spaExcelColumn(SpaExcelHeader.TaxName, SpaExcelSourceField.TaxName),
    spaExcelColumn(
      SpaExcelHeader.AllowTaxOnGratuity,
      SpaExcelSourceField.AllowTaxOnGratuity,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.AllowTaxOnServiceCharge,
      SpaExcelSourceField.AllowTaxOnServiceCharge,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsDefaultTax,
      SpaExcelSourceField.DefaultToAllRetailItems,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsAmountOrPercentage,
      SpaExcelSourceField.PercentageOrFlatAmount,
    ),
    spaExcelColumn(SpaExcelHeader.TaxValue, SpaExcelSourceField.Value),
    spaExcelColumn(
      SpaExcelHeader.StartDate,
      SpaExcelSourceField.StartDate,
      EXCEL_DEFAULT_START_DATE,
    ),
    spaExcelColumn(
      SpaExcelHeader.EndDate,
      SpaExcelSourceField.EndDate,
      EXCEL_DEFAULT_END_DATE_PLUS_3_YEARS,
    ),
  ]),

  spaExcelSheet(
    SpaPropertyEnum.retailSubCategories,
    SpaExcelSheetName.SubCategory,
    [
      spaExcelColumn(SpaExcelHeader.Name, SpaExcelSourceField.SubCategoryName),
      spaExcelColumn(
        SpaExcelHeader.Level,
        SpaExcelSourceField.SubCategoryNumber,
        1,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
  ),

  spaExcelSheet(
    SpaPropertyEnum.retailItems,
    SpaExcelSheetName.RetailItem,
    retailItemExcelColumns,
    SpaExcelSheetName.RetailItem,
    mapRetailItemRows,
  ),

  spaExcelSheet(SpaPropertyEnum.vendorTypes, SpaExcelSheetName.VendorType, [
    spaExcelColumn(SpaExcelHeader.VendorType, SpaExcelSourceField.VendorType),
    spaExcelColumn(
      SpaExcelHeader.IsActive,
      ExcelEmptyDefault.Empty,
      ExcelBooleanString.True,
    ),
  ]),

  spaExcelFile(SpaPropertyEnum.vendorSetup, SpaExcelSheetName.Vendor, [
    spaExcelWorksheet(
      SpaExcelSheetName.Vendor,
      [
        spaExcelColumn(
          SpaExcelHeader.VendorCode,
          SpaExcelSourceField.VendorCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.VendorName,
          SpaExcelSourceField.VendorName,
        ),
        spaExcelColumn(
          SpaExcelHeader.VendorType,
          SpaExcelSourceField.VendorType,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsActive,
          ExcelEmptyDefault.Empty,
          ExcelBooleanString.True,
        ),
        spaExcelColumn(
          SpaExcelHeader.RepresentativeFname,
          SpaExcelSourceField.FirstName,
        ),
        spaExcelColumn(
          SpaExcelHeader.RepresentativeLname,
          SpaExcelSourceField.LastName,
        ),
        spaExcelColumn(
          SpaExcelHeader.Comments,
          SpaExcelSourceField.Comments,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.AddressLine1,
          SpaExcelSourceField.Address,
        ),
        spaExcelColumn(
          SpaExcelHeader.AddressLine2,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.AddressLine3,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(SpaExcelHeader.City, SpaExcelSourceField.City),
        spaExcelColumn(SpaExcelHeader.State, SpaExcelSourceField.State),
        spaExcelColumn(SpaExcelHeader.Country, SpaExcelSourceField.Country),
        spaExcelColumn(
          SpaExcelHeader.PostalCode,
          SpaExcelSourceField.PostalCode,
        ),
      ],
      ExcelBoolean.False,
      mapVendorSetupVendorRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.VendorContactInformation,
      [
        spaExcelColumn(
          SpaExcelHeader.VendorCode,
          SpaExcelSourceField.VendorCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsPrimary,
          SpaExcelSourceField.IsPrimary,
          ExcelBooleanString.True,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsPrivateInfo,
          SpaExcelSourceField.IsPrivateInfo,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.ContactType,
          SpaExcelSourceField.ContactType,
          DEFAULT_VENDOR_CONTACT_TYPE,
        ),
        spaExcelColumn(SpaExcelHeader.Value, SpaExcelSourceField.Value),
      ],
      ExcelBoolean.False,
      mapVendorSetupContactRows,
    ),
  ]),

  // spaExcelSheet(SpaPropertyEnum.paymentMethods, SpaExcelSheetName.PaymentMethod, [
  //   spaExcelColumn(SpaExcelHeader.PaymentMethodName, SpaExcelSourceField.PaymentMethodName),
  //   spaExcelColumn(SpaExcelHeader.ListOrder, ExcelEmptyDefault.Empty, 1),
  //   spaExcelColumn(SpaExcelHeader.IsActive, SpaExcelSourceField.Active),
  //   spaExcelColumn(SpaExcelHeader.RequireComment, ExcelEmptyDefault.Empty, ExcelBooleanString.False),
  //   spaExcelColumn(SpaExcelHeader.RequireSignatureCapture, ExcelEmptyDefault.Empty, ExcelBooleanString.False),
  //   spaExcelColumn(SpaExcelHeader.RequireReceipts, ExcelEmptyDefault.Empty, ExcelBooleanString.False),
  //   spaExcelColumn(SpaExcelHeader.NumberOfReceipts, ExcelEmptyDefault.Empty, 1),
  // ]),

  spaExcelSheet(
    SpaPropertyEnum.guestTypes,
    SpaExcelSheetName.GuestType,
    [
      spaExcelColumn(
        SpaExcelHeader.GuestTypeCode,
        SpaExcelSourceField.GuestTypeCode,
      ),
      spaExcelColumn(
        SpaExcelHeader.GuestTypeDescription,
        SpaExcelSourceField.GuestTypeName,
      ),
      spaExcelColumn(SpaExcelHeader.ListOrder, SpaExcelSourceField.ListOrder),
    ],
    SpaExcelSheetName.GuestType,
    mapGuestTypeExportRows,
  ),

  spaExcelSheet(SpaPropertyEnum.priceTypes, SpaExcelSheetName.PriceTypes, [
    spaExcelColumn(SpaExcelHeader.Code, SpaExcelSourceField.PriceTypeCode),
    spaExcelColumn(
      SpaExcelHeader.Description,
      SpaExcelSourceField.PriceTypeDescription,
    ),
  ]),
  spaExcelSheet(SpaPropertyEnum.logTypes, SpaExcelSheetName.LogType, [
    spaExcelColumn(SpaExcelHeader.LogType, SpaExcelSourceField.LogTypeName),
    spaExcelColumn(
      SpaExcelHeader.IsActive,
      SpaExcelSourceField.Active,
      ExcelBoolean.True,
    ),
  ]),

  spaExcelSheet(SpaPropertyEnum.addOns, SpaExcelSheetName.AddOn, [
    spaExcelColumn(SpaExcelHeader.AddOnName, SpaExcelSourceField.AddOnName),
    spaExcelColumn(
      SpaExcelHeader.Commisionable,
      SpaExcelSourceField.Commissionable,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.IsAvailableOnWeb,
      SpaExcelSourceField.AvailableOnWeb,
      ExcelBooleanString.False,
    ),
    spaExcelColumn(
      SpaExcelHeader.RetailCategoryName,
      SpaExcelSourceField.RetailCategory,
      ExcelEmptyDefault.Empty,
    ),
    spaExcelColumn(
      SpaExcelHeader.RetailItemNumber,
      SpaExcelSourceField.RetailItemNumber,
      DEFAULT_RETAIL_ITEM_NUMBER,
    ),
    spaExcelColumn(SpaExcelHeader.Price, SpaExcelSourceField.Price, "0.00"),
  ]),
  spaExcelFile(SpaPropertyEnum.therapistSetup, SpaExcelSheetName.Therapist, [
    spaExcelWorksheet(
      SpaExcelSheetName.Therapist,
      [
        spaExcelColumn(
          SpaExcelHeader.TherapistCode,
          SpaExcelSourceField.StaffCode,
        ),
        spaExcelColumn(SpaExcelHeader.FirstName, SpaExcelSourceField.FirstName),
        spaExcelColumn(SpaExcelHeader.LastName, SpaExcelSourceField.LastName),
        spaExcelColumn(SpaExcelHeader.Gender, SpaExcelSourceField.Gender),
        spaExcelColumn(
          SpaExcelHeader.DateOfHire,
          SpaExcelSourceField.DateOfHire,
        ),
        spaExcelColumn(
          SpaExcelHeader.SeniorityLevel,
          SpaExcelSourceField.SeniorityLevel,
          0,
        ),
        spaExcelColumn(SpaExcelHeader.AKA, SpaExcelSourceField.AKA),
        spaExcelColumn(
          SpaExcelHeader.AllowGratuity,
          SpaExcelSourceField.AllowGratuity,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.AllowServiceCharge,
          SpaExcelSourceField.AllowServiceCharge,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.AllowCommission,
          SpaExcelSourceField.AllowCommission,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsActive,
          ExcelEmptyDefault.Empty,
          ExcelBooleanString.True,
        ),
        spaExcelColumn(SpaExcelHeader.ListOrder, ExcelEmptyDefault.Empty, 0),
        spaExcelColumn(
          SpaExcelHeader.CommissionClassId,
          SpaExcelSourceField.CommissionClass,
          0,
        ),
        spaExcelColumn(
          SpaExcelHeader.DisplayOnAppointmentGrid,
          SpaExcelSourceField.DisplayOnAppointmentGrid,
          ExcelBooleanString.True,
        ),
        spaExcelColumn(
          SpaExcelHeader.HourlyRate,
          SpaExcelSourceField.HourlyRate,
          "0.00",
        ),
      ],
      ExcelBoolean.False,
      mapTherapistMainRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.ServiceTherapist,
      [
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.TherapistCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.SeniorityLevel,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceCount,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      ExcelBoolean.True,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.TherapistContact,
      [
        spaExcelColumn(
          SpaExcelHeader.TherapistCode,
          SpaExcelSourceField.StaffCode,
        ),
        spaExcelColumn(SpaExcelHeader.Type, SpaExcelSourceField.ContactType),
        spaExcelColumn(
          SpaExcelHeader.ContactLocation,
          SpaExcelSourceField.ContactLocation,
        ),
        spaExcelColumn(
          SpaExcelHeader.ContactDetail,
          SpaExcelSourceField.ContactDetail,
        ),
      ],
      ExcelBoolean.False,
      mapTherapistContactRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.TherapistAddOn,
      [
        spaExcelColumn(
          SpaExcelHeader.TherapistCode,
          SpaExcelSourceField.StaffCode,
        ),
        spaExcelColumn(SpaExcelHeader.AddOnName, SpaExcelSourceField.Empty),
      ],
      ExcelBoolean.True,
    ),
  ]),

  spaExcelFile(SpaPropertyEnum.services, SpaExcelSheetName.ServiceDetail, [
    spaExcelWorksheet(
      SpaExcelSheetName.ServiceDetail,
      [
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          SpaExcelSourceField.ServiceCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceDescription,
          SpaExcelSourceField.ServiceDescription,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceGroupCode,
          SpaExcelSourceField.ServiceGroupCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.EffectiveFromDate,
          SpaExcelSourceField.EffectiveFromDate,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.EffectiveToDate,
          SpaExcelSourceField.EffectiveToDate,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServicePrice,
          SpaExcelSourceField.ServicePrice,
          "0.00",
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceTime,
          SpaExcelSourceField.ServiceTime,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.SetupTime,
          SpaExcelSourceField.SetupTime,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.BreakdownTime,
          SpaExcelSourceField.BreakdownTime,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ListOrder,
          SpaExcelSourceField.ListOrder,
          1,
        ),
        spaExcelColumn(
          SpaExcelHeader.MinimumAge,
          SpaExcelSourceField.MinimumAge,
          "0",
        ),
        spaExcelColumn(
          SpaExcelHeader.MinimumGuest,
          SpaExcelSourceField.MinimumGuests,
          "0",
        ),
        spaExcelColumn(
          SpaExcelHeader.MaximumGuest,
          SpaExcelSourceField.MaximumGuests,
          "1",
        ),
        spaExcelColumn(
          SpaExcelHeader.MinimumStaff,
          SpaExcelSourceField.MinimumStaff,
          "0",
        ),
        spaExcelColumn(
          SpaExcelHeader.MaximumStaff,
          SpaExcelSourceField.MaximumStaff,
          "1",
        ),
        spaExcelColumn(
          SpaExcelHeader.OffSite,
          SpaExcelSourceField.Offsite,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.AutoGratuity,
          SpaExcelSourceField.AutoGratuity,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.GratuityPercent,
          SpaExcelSourceField.AutoGratuity2,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.GratuityAmount,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.AutoServiceCharge,
          SpaExcelSourceField.AutoServiceCharge,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceChargePercent,
          SpaExcelSourceField.AutoServiceCharge2,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceChargeAmount,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.Commissionable,
          SpaExcelSourceField.Commissionable,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceComments,
          SpaExcelSourceField.ServiceComment,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServicePolicy,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.CancellationPolicy,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.LinkedRetailItemCode,
          SpaExcelSourceField.LinkedItem,
          DEFAULT_RETAIL_ITEM_NUMBER,
        ),
        spaExcelColumn(
          SpaExcelHeader.AvailableOnWeb,
          SpaExcelSourceField.AvailableOnWeb,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsInactive,
          SpaExcelSourceField.Inactive,
          ExcelBooleanString.False,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceColorCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServicePoints,
          SpaExcelSourceField.ServicePoints,
          0,
        ),
        spaExcelColumn(
          SpaExcelHeader.TherapistCodes,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.LocationCodes,
          SpaExcelSourceField.ServiceLocations,
        ),
        spaExcelColumn(
          SpaExcelHeader.AddOns,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.MedicalConditionCodes,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      ExcelBoolean.False,
      mapServiceDetailRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.ServiceAvailableDays,
      serviceAvailableDaysColumns,
      ExcelBoolean.False,
      mapServiceAvailableDaysRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.ServiceEquipment,
      [
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          SpaExcelSourceField.ServiceCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.EquipmentCode,
          SpaExcelSourceField.EquipmentCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.RequireQuantity,
          SpaExcelSourceField.RequireQuantity,
          0,
        ),
      ],
      ExcelBoolean.False,
      mapServiceEquipmentRows,
      undefined,
      ExcelBoolean.True,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.ServicePriceType,
      [
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          SpaExcelSourceField.ServiceCode,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceTypeCode,
          SpaExcelSourceField.PriceTypeCode,
        ),
        spaExcelColumn(SpaExcelHeader.Price, SpaExcelSourceField.Price),
      ],
      ExcelBoolean.False,
      mapServicePriceTypeRows,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.ServiceSeasonalDate,
      [
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.StartDate,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.EndDate,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceMon,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceTue,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceWed,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceThu,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceFri,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceSat,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.PriceSun,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      ExcelBoolean.True,
    ),
  ]),

  spaExcelFile(SpaPropertyEnum.dayPackages, SpaExcelSheetName.PackageDetail, [
    spaExcelWorksheet(SpaExcelSheetName.PackageDetail, [
      spaExcelColumn(
        SpaExcelHeader.PackageCode,
        SpaExcelSourceField.PackageCode,
      ),
      spaExcelColumn(
        SpaExcelHeader.PackageDescription,
        SpaExcelSourceField.PackageDescription,
      ),
      spaExcelColumn(
        SpaExcelHeader.PackageStartDate,
        SpaExcelSourceField.EffectiveFromDate,
      ),
      spaExcelColumn(
        SpaExcelHeader.PackageEndDate,
        SpaExcelSourceField.EffectiveToDate,
      ),
      spaExcelColumn(SpaExcelHeader.PackagePrice, SpaExcelSourceField.Price),
      spaExcelColumn(
        SpaExcelHeader.NoOfPeoples,
        SpaExcelSourceField.MinimumGuests,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.MaximumNoOfDays,
        SpaExcelSourceField.MaximumGuests,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.PackagePoints,
        SpaExcelSourceField.Points,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.LinkedRetailItemCode,
        SpaExcelSourceField.LinkedItem,
        DEFAULT_RETAIL_ITEM_NUMBER,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsActive,
        SpaExcelSourceField.Active,
        ExcelBoolean.False,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsAutomaticRecap,
        ExcelEmptyDefault.Empty,
        ExcelBoolean.False,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsSelectCompAtBooking,
        ExcelEmptyDefault.Empty,
        ExcelBoolean.False,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsTopCompAtBooking,
        ExcelEmptyDefault.Empty,
        ExcelBoolean.False,
      ),
      spaExcelColumn(
        SpaExcelHeader.NumberOfItems,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.NumberOfRetailItems,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.PackagePolicy,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      spaExcelColumn(
        SpaExcelHeader.IsDefaultCancelPolicy,
        ExcelEmptyDefault.Empty,
        ExcelBoolean.False,
      ),
      spaExcelColumn(
        SpaExcelHeader.CancellationPolicy,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
    ]),
    spaExcelWorksheet(SpaExcelSheetName.PackageAvailableDays, [
      spaExcelColumn(
        SpaExcelHeader.PackageCode,
        SpaExcelSourceField.PackageCode,
      ),
      ...dayAvailabilityColumns(),
    ]),
    spaExcelWorksheet(
      SpaExcelSheetName.PackageComponents,
      [
        spaExcelColumn(
          SpaExcelHeader.PackageCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.ServiceCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.Quantity,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.RetailItemNumber,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsFixedComponent,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.IncrementDecrement,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.Percentage,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        spaExcelColumn(
          SpaExcelHeader.AllowPriceChange,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      ExcelBoolean.True,
    ),
    spaExcelWorksheet(
      SpaExcelSheetName.PackageYield,
      [
        spaExcelColumn(
          SpaExcelHeader.PackageCode,
          SpaExcelSourceField.PackageCode,
        ),
        spaExcelColumn(SpaExcelHeader.PackageClass),
        spaExcelColumn(SpaExcelHeader.Available),
        spaExcelColumn(
          SpaExcelHeader.StartDate,
          SpaExcelSourceField.EffectiveFromDate,
        ),
        spaExcelColumn(
          SpaExcelHeader.EndDate,
          SpaExcelSourceField.EffectiveToDate,
        ),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnMonday,
          SpaExcelSourceField.AvailableMon,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceMonday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnTuesday,
          SpaExcelSourceField.AvailableTue,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceTuesday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnWednesday,
          SpaExcelSourceField.AvailableWed,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceWednesday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnThursday,
          SpaExcelSourceField.AvailableThu,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceThursday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnFriday,
          SpaExcelSourceField.AvailableFri,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceFriday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnSaturday,
          SpaExcelSourceField.AvailableSat,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceSaturday, SpaExcelSourceField.N000),
        spaExcelColumn(
          SpaExcelHeader.IsAvailableOnSunday,
          SpaExcelSourceField.AvailableSun,
          ExcelBoolean.False,
        ),
        spaExcelColumn(SpaExcelHeader.PriceSunday, SpaExcelSourceField.N000),
      ],
      ExcelBoolean.True,
    ),
  ]),
];
